/**
 * Read-only detail view for one deal.
 *
 * The edit sheet answers "what can I change?"; this answers "what is this
 * deal?". Clicking a row on the pipeline lands here rather than in the form,
 * because reading a deal is the common case and editing is the exception —
 * the footer carries an Edit button for when it is not.
 */
import { useApp } from '../../store/AppContext.jsx';
import { parseDate, daysTo, daysAgo } from '../../lib/dates.js';
import Icon from '../../lib/icons.jsx';
import { spent } from '../../lib/format.js';
import Sheet from '../Sheet.jsx';
import PlanChip from '../PlanChip.jsx';
import DealJourney from '../DealJourney.jsx';
import LeadCapture from '../LeadCapture.jsx';
import AskAI from '../AskAI.jsx';
import {
  Avatar, OwnerPill, OriginPill, PlanPill, Pill, Dash, EmptyState, SegmentPill
} from '../Primitives.jsx';
import {
  companyName, contactOf, probOf, weighted, isOpen, isStalled, isDeal,
  dealActivities, lastTouch, clientOfDeal, isConverted, leadDetails
} from '../../domain/crm.js';

/**
 * "5 pages · 32 clicks · 4 scrolls", in the order somebody asks for it.
 *
 * Kinds with nothing in them are left out, so a plain pageview visit reads as
 * one phrase rather than three zeroes.
 */
const EVENT_ORDER = [
  ['pageview', 'page', 'pages'],
  ['click', 'click', 'clicks'],
  ['scroll', 'scroll', 'scrolls'],
  ['exit', 'exit', 'exits']
];
const eventSummary = n => EVENT_ORDER
  .filter(([k]) => n[k])
  .map(([k, one, many]) => `${n[k]} ${n[k] === 1 ? one : many}`)
  .join(' \u00b7 ');

/*
 * The counts, from whichever shape arrived.
 *
 * A list response carries `eventStats` and leaves the events behind — thirty
 * thousand click records is 70% of everything the board downloads and no list
 * draws one of them. A single deal read carries the events themselves. This
 * panel is an index either way; the window it opens goes and gets the trail.
 */
const statsOf = v => {
  if (v.eventStats) return v.eventStats;
  const events = v.events || [];
  const n = { total: events.length };
  events.forEach(e => { n[e.type] = (n[e.type] || 0) + 1; });
  return n;
};

/** One label/value line. Values fall back to a dash so rows never collapse. */
const Row = ({ label, children }) => (
  <div className="vrow">
    <span className="vlab">{label}</span>
    <span className="vval">{children ?? <Dash />}</span>
  </div>
);

const Panel = ({ icon, tone, title, children, wide }) => (
  <section className={`vpanel${wide ? ' wide' : ''}`}>
    <h4><span className={`ic ${tone}`}><Icon name={icon} /></span>{title}</h4>
    <div className="vbody">{children}</div>
  </section>
);

/* Where a stage sits in the funnel decides how its pill reads. */
const STAGE_TONE = {
  Won: 'gr-green', Lost: 'gr-red', Negotiation: 'gr-amber', Proposal: 'gr-amber'
};

export default function DealView({ id, onClose }) {
  const { S, crm, rows, fmtMoney, fmtDate, setSheet, openDraft } = useApp();
  const d = crm.deals.find(x => x.id === id);

  if (!d) {
    return (
      <Sheet icon="eye" title="Lead" onClose={onClose} closeLabel="Close">
        <div style={{ gridColumn: '1/-1' }}>
          <EmptyState icon="alert" tone="g-red" title="That row is no longer in the pipeline">
            It may have been deleted, or filtered out.
          </EmptyState>
        </div>
      </Sheet>
    );
  }

  const close = parseDate(d.close);
  const overdue = close && isOpen(d) && daysTo(close) < 0;
  const stale = isStalled(S, crm, d);
  const acts = dealActivities(crm, id);
  const touched = lastTouch(crm, id);
  const linked = clientOfDeal(rows, d);
  const needsConvert = d.stage === 'Won' && !isConverted(rows, d);

  /* Still on the board, as opposed to already won and sitting on Deals.
     The two pages show the same record and want different things from it. */
  const onLead = !isDeal(d);
  const contact = contactOf(crm, d.contact);
  const f = leadDetails(crm, d);
  const requested = parseDate(f.requested);
  const lastAt = parseDate(f.lastSeen);

  /* Only tiles that carry a figure. A demo request has no value until
     somebody prices it and no probability that means anything, so those
     tiles come and go with the numbers behind them. */
  const tiles = [
    +d.value ? [fmtMoney(d.value), 'Value'] : null,
    isOpen(d) && +d.value ? [`${probOf(S, d)}%`, 'Probability'] : null,
    isOpen(d) && +d.value ? [fmtMoney(Math.round(weighted(S, d))), 'Weighted'] : null,
    requested ? [fmtDate(requested), 'Requested'] : null,
    close && d.stage === 'Won' ? [fmtDate(close), 'Won on'] : null,
    close && isOpen(d) ? [fmtDate(close),
      `Close · ${overdue ? `${Math.abs(daysTo(close))}d late` : `in ${daysTo(close)}d`}`] : null
  ].filter(Boolean);

  const hero = (
    <div className="vhero">
      <div className="vhero-in">
        <Avatar name={d.title} className="vava" />
        <div className="vwho">
          <b>{d.title || 'Untitled deal'}</b>
          <span>{companyName(crm, d.cid) || 'no company linked'}</span>
          {/* On the panel too, so the badge does not vanish the moment
              somebody opens the row to check it. */}
          <SegmentPill value={d.segment} />
        </div>
      </div>

      <div className="vstats">
        {tiles.map(([value, label]) => (
          <div className="vstat" key={label}>
            <span>{value}</span>
            <small>{label}</small>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Sheet
      icon="eye"
      tone="g-cyan"
      title={onLead ? 'Lead details' : 'Deal details'}
      onClose={onClose}
      closeLabel="Close"
      bodyClass="viewbody"
      wide
      hero={hero}
      /* The AI read answers the question the footer buttons assume you
         have already settled - whether this is worth any of your
         morning - so it belongs at the top of the panel with the way
         out, not at the bottom among the things that change something. */
      headActions={(
        <AskAI
          module={onLead ? 'lead' : 'deal'}
          id={d.id}
          subject={d.title}
          label={onLead ? 'Read this lead' : 'Coach this deal'}
        />
      )}
      footerLeft={
        <>
          {/* The things anybody does after reading this. They were on the
              pipeline row and nowhere else, so opening the lead to decide
              meant closing it again to act. */}
          {/* On both boards. A deal is chased exactly as hard as a lead —
              harder — and the button was on the pipeline only, so the whole
              second half of the chase had nowhere to be written down from
              the panel where it is read. */}
          <button
            type="button"
            className="btn"
            onClick={() => setSheet({ kind: 'activity', dealId: d.id })}
          >
            <Icon name="cal" />Log an activity
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => setSheet({ kind: 'deal', id: d.id })}
          >
            <Icon name="edit" />{onLead ? 'Edit lead' : 'Edit deal'}
          </button>
          {/* They said no. Beside the two that assume they said yes, because
              the moment somebody decides is the moment they are reading
              this, and making them close the panel to record it is how the
              reason ends up never being written down at all. */}
          {!d.dropped ? (
            <button
              type="button"
              className="btn danger"
              onClick={() => setSheet({ kind: 'notInterested', id: d.id })}
            >
              <Icon name="x" />Not interested
            </button>
          ) : null}
          {/* Not a no — nobody we could reach. Its own button so the two
              are counted apart on the report. */}
          {!d.dropped ? (
            <button
              type="button"
              className="btn"
              onClick={() => setSheet({ kind: 'noResponse', id: d.id })}
            >
              <Icon name="eyeoff" />No response
            </button>
          ) : null}
          {/*
            * Straight onto the ledger, from either board.
            *
            * A lead does not have to become a deal first. Somebody who rings
            * back and pays on the call is a customer, and routing that
            * through a deal stage nobody worked is bookkeeping for its own
            * sake. Both boards check the client book before moving anything.
            */}
          {!d.dropped && !d.clientId ? (
            <button
              type="button"
              className="btn"
              onClick={() => setSheet({
                kind: 'moveToClient', id: d.id, from: onLead ? 'pipeline' : 'deal'
              })}
            >
              <Icon name="users" />Move to client
            </button>
          ) : null}
          {onLead && !d.dropped ? (
            <button
              type="button"
              className="btn primary"
              onClick={() => setSheet({ kind: 'moveToDeal', id: d.id })}
            >
              <Icon name="check" />Move to deal
            </button>
          ) : null}
        </>
      }
    >
      {stale || overdue || needsConvert ? (
        <Panel icon="alert" tone="g-red" title="Needs attention" wide>
          <div className="vflags">
            {overdue ? (
              <Pill tone="gr-red" icon="alert">
                {`Close date passed ${Math.abs(daysTo(close))}d ago`}
              </Pill>
            ) : null}
            {stale ? (
              <Pill tone="gr-amber" icon="flag">
                {touched ? `No activity in ${daysAgo(touched)}d` : 'Never touched'}
              </Pill>
            ) : null}
            {needsConvert ? (
              <Pill tone="gr-amber" icon="star">Won, but no client record yet</Pill>
            ) : null}
          </div>
        </Panel>
      ) : null}

      {/* What the tracker saw, first: it is what the person opening a demo
          request wants to know, and it is all real - nothing here is a
          default or a guess. */}
      <Panel icon="zap" tone="g-cyan" title="From the tracker" wide>
        <Row label="Name">{d.title || null}</Row>
        <Row label="Email">{f.email ? <a href={`mailto:${f.email}`}>{f.email}</a> : null}</Row>
        <Row label="Phone">{f.phone || null}</Row>
        <Row label="Company">{companyName(crm, d.cid) || null}</Row>
        {/* Which form, and whether it went through. Partially Submitted does
            not mean it failed: the tracker only marks a capture submitted when
            it recognises the thank-you, and plenty of forms redirect or post
            by XHR instead. It means nobody watched it land. */}
        <Row label="Enquiry">{f.type || null}</Row>
        <Row label="Form">
          {f.stateLabel
            ? <Pill tone={f.state === 'submitted' ? 'gr-green' : 'gr-amber'}>{f.stateLabel}</Pill>
            : null}
        </Row>
        {/* Everything they have ever filled in, when it is more than the one
            this row is filed under. */}
        <Row label="Also did">
          {f.types.filter(t => t !== f.type).join(', ') || null}
        </Row>
        <Row label="Location">{f.place || null}</Row>
        {/* With the clock. The tracker stamps the minute, and "23 Sep 2026"
            on four hundred rows says less than the order they are already
            in. */}
        <Row label="Requested on">{requested ? fmtDate(requested, true) : null}</Row>
        {/* The number, even when it is one. A dash here reads as "we do
             not know", and we do. */}
        <Row label="Visits">
          {f.visits
            ? `${f.visits}${f.visits > 1 && lastAt ? ` · last ${fmtDate(lastAt, true)}` : ''}`
            : null}
        </Row>
        <Row label="Source">{f.sources.join(', ') || d.source || null}</Row>
        <Row label="Campaign">{f.campaign || null}</Row>
        {/* The other four UTM tags. Rolled into one line because they are
            read together or not at all, and four empty rows on the majority
            of leads is four rows of nothing. */}
        <Row label="UTM">
          {[
            f.utm.source && `source=${f.utm.source}`,
            f.utm.medium && `medium=${f.utm.medium}`,
            f.utm.term && `term=${f.utm.term}`,
            f.utm.content && `content=${f.utm.content}`
          ].filter(Boolean).join(' · ') || null}
        </Row>
        <Row label="Interested in">{f.products || null}</Row>
        <Row label="Landed on">
          {f.landing ? <a href={f.landing} target="_blank" rel="noreferrer">{f.landingPath}</a> : null}
        </Row>
        <Row label="Referrer">
          {f.referrer
            ? <a href={f.referrer} target="_blank" rel="noreferrer">{f.referrer}</a>
            : null}
        </Row>
        <Row label="Device">{[f.device, f.os, f.browser].filter(Boolean).join(' · ') || null}</Row>
        {/* Which platform sent them, and which ad they clicked. The click id
            itself is three hundred characters of opaque token and is not
            shown — the network is the part a person can act on. */}
        <Row label="Social">
          {f.social.length
            ? f.social.map(x => [x.platform, x.label].filter(Boolean).join(' · ')).join(' / ')
            : null}
        </Row>
        <Row label="Ad click">
          {f.adClicks.length
            ? f.adClicks.map(a => `${a.type}${a.at ? ` · ${fmtDate(parseDate(a.at), true)}` : ''}`).join(', ')
            : null}
        </Row>
        {/* Labelled as the network it is. The tracker resolves this from the
            visitor's IP address, so it is usually their internet provider and
            not their employer, and it has been read as the latter before. */}
        <Row label="Network">
          {[f.network, f.networkDomain].filter(Boolean).join(" · ") || null}
        </Row>
        {/* A guess, and labelled as one. The tracker asks Gemini to work it
            out from the address, so it is empty far more often than not and
            is never a reason to believe anything. */}
        <Row label="LinkedIn (guess)">
          {f.linkedin
            ? <a href={f.linkedin} target="_blank" rel="noreferrer">{f.linkedin}</a>
            : null}
        </Row>
        <Row label="Google">{f.google && f.google.id ? 'Signed in with Google' : null}</Row>
        <Row label="Came from"><OriginPill value={d.origin} /></Row>
      </Panel>

      {/*
        * Every visit they made, in order, with the pages they read.
        *
        * This was a <details> with one line per visit inside "The request" —
        * folded shut, at the bottom, under a summary that said "5 visits" and
        * nothing about them. It is the richest thing the tracker returns and
        * the only part of this screen that answers "are they actually
        * interested": five visits to /pricing over two days, four minutes on
        * the last one, is a call to make this morning.
        *
        * So it is a panel, open, with the journey under each visit. Newest
        * first, because that is the one that matters and the one somebody
        * scrolls to otherwise.
        */}
      {/*
        * What the tracker saw, still here after it was won.
        *
        * The visits, the attribution and the page journey were on the
        * pipeline row, and a lead moved to Deals leaves that board — so the
        * one screen that explains where a won deal came from disappeared at
        * the moment it started mattering. It is the same panel, reading the
        * same `lead_intel`, and the journey opens in the same window.
        */}
      {/*
        * The page journey, findable.
        *
        * It is inside the capture panel below — one line per visit, at the
        * bottom of a card that is a screen tall by the time the fields, the
        * location and the attribution are on it. On a deal, which is read
        * from the top to answer "where did this come from", it was below the
        * fold and nobody found it.
        *
        * So the visits are indexed here, at the top: one line each, straight
        * into the same window. Not a second copy of the trail — a way in.
        */}
      {(f.sessions || []).some(v => statsOf(v).total) ? (
        <Panel icon="flow" tone="g-indigo" title="Page journey" wide>
          <div className="vpj">
            {(f.sessions || []).map((v, i) => {
              const n = statsOf(v);
              if (!n.total) return null;
              const at = parseDate(v.at);
              return (
                <button
                  key={`${v.at}-${i}`}
                  type="button"
                  className="vpj-line"
                  onClick={() => setSheet({
                    kind: 'pageJourney',
                    visit: v,
                    /* Where the window goes for the trail, which the list
                       response does not carry. */
                    dealId: d.id,
                    index: i,
                    who: d.title,
                    at: at ? fmtDate(at, true) : ''
                  })}
                >
                  <Icon name="flow" />
                  <b>{`${n.total} event${n.total === 1 ? '' : 's'}`}</b>
                  <span>{eventSummary(n)}</span>
                  <em>{at ? fmtDate(at, true) : ''}</em>
                  <Icon name="chev" />
                </button>
              );
            })}
          </div>
        </Panel>
      ) : null}

      {(f.sessions || []).length ? (
        <Panel icon="flow" tone="g-blue" title="What the tracker saw" wide>
          <LeadCapture f={f} name={d.title} fmtDate={fmtDate} />
        </Panel>
      ) : null}
      <Panel icon="flag" tone="g-green" title={onLead ? 'Where it sits' : 'Deal'}>
        <Row label="Stage">
          <Pill tone={STAGE_TONE[d.stage] || 'gr-slate'}>{d.stage}</Pill>
        </Row>
        <Row label="Value">{+d.value ? fmtMoney(d.value) : null}</Row>
        {isOpen(d) && +d.value ? <Row label="Probability">{`${probOf(S, d)}%`}</Row> : null}
        <Row label={d.stage === 'Won' ? 'Won on' : 'Expected close'}>{close ? fmtDate(close) : null}</Row>
        <Row label="Plan">{[d.plan, d.cycle].filter(Boolean).join(' · ') || null}</Row>
        <Row label="Next step">{d.next || null}</Row>
      </Panel>

      <Panel icon="building" tone="g-blue" title="Who it is with">
        <Row label="Company">{companyName(crm, d.cid) || null}</Row>
        {/*
          * The contact record if there is one, the tracker's own answer if
          * not.
          *
          * These read as dashes on every tracked lead, because the sync
          * deliberately does not file a contact for each form submission -
          * Contacts is a list the team keeps by hand. So the panel said we
          * had no address for somebody whose address is printed four inches
          * above it, under "From the tracker". One of the two was wrong, and
          * it was this one.
          */}
        <Row label="Contact">{(contact && contact.name) || null}</Row>
        <Row label="Email">
          {contact && contact.email
            ? <a href={`mailto:${contact.email}`}>{contact.email}</a>
            : f.email ? <a href={`mailto:${f.email}`}>{f.email}</a> : null}
        </Row>
        <Row label="Phone">
          {contact && contact.phone
            ? <a href={`tel:${contact.phone}`}>{contact.phone}</a>
            : f.phone ? <a href={`tel:${f.phone}`}>{f.phone}</a> : null}
        </Row>
        <Row label="Owner"><OwnerPill name={d.owner} /></Row>
      </Panel>

      <Panel icon="card" tone="g-orange" title="What is being sold">
        {/* "Books +5" names one of six. The chip opens on the rest. */}
        <Row label="Plan discussed">
          {f.productList.length > 1
            ? <PlanChip list={f.productList} label={d.plan} />
            : d.plan ? <PlanPill value={d.plan} /> : null}
        </Row>
        <Row label="Billing cycle">{d.cycle || null}</Row>
        <Row label="Client record">
          {linked
            ? linked.name
            : d.stage === 'Won'
              ? <Pill tone="gr-amber">Not created yet</Pill>
              : null}
        </Row>
      </Panel>

      <Panel icon="clock" tone="g-pink" title="Momentum">
        <Row label="Activities logged">{acts.length || null}</Row>
        <Row label="Last touch">{touched ? `${daysAgo(touched)}d ago` : null}</Row>
        <Row label="Next step">{d.next || null}</Row>
      </Panel>

      {/* The activity list used to be here, newest first and capped at
          eight. It answered "what was logged" and never "what happened":
          the day they arrived, the demo, the rounds of chasing after it
          and the day they started paying all live in other places, and
          nobody was going to open four screens to put them in order. */}
      {/*
        * Chasing is the pipeline's job, not this page's.
        *
        * A row that has reached Deals has been won: the next thing it needs
        * is a client record, not another call logged against it and not a
        * drafted email. Both buttons stay on a lead, where the work is
        * still to do, and the trail here is the record of how it got won.
        */}
      <Panel icon="zap" tone="g-purple" title={onLead ? 'What has been done' : 'Journey'} wide>
        <DealJourney
          deal={d}
          limit={12}
          onLog={onLead ? () => { onClose(); setSheet({ kind: 'activity', dealId: d.id }); } : undefined}
          onEdit={actId => { onClose(); setSheet({ kind: 'activity', id: actId }); }}
        />

        {onLead ? (
          <div className="vquick">
            <button
              type="button"
              className="btn sm"
              onClick={() => { onClose(); openDraft({ kind: 'deal', id: d.id }); }}
            >
              <Icon name="wand" />Draft outreach
            </button>
          </div>
        ) : null}
      </Panel>

      {/*
        * The request, laid out rather than transcribed.
        *
        * The tracker writes this as three run-on lines - "Demo requested
        * 2026-09-03. Contact: Zabir Ahmed · zesdxb@gmail.com · 971507782850"
        * and so on - which is a fine way to store it and a terrible way to
        * read it: six products, a campaign, a city and a country all in one
        * paragraph with middots for punctuation. Every fact in it is already
        * pulled out by leadDetails, so the panel shows them as facts and
        * keeps the original underneath for anything the parser missed.
        */}
      <Panel icon="file" tone="g-slate" title="The request" wide>
        {d.notes ? (
          <>
            <div className="reqgrid">
              {requested
                ? <div><dt>Asked on</dt><dd>{fmtDate(requested, true)}</dd></div> : null}
              {f.email ? (
                <div>
                  <dt>Email</dt>
                  <dd><a href={`mailto:${f.email}`}>{f.email}</a></dd>
                </div>
              ) : null}
              {f.phone ? <div><dt>Phone</dt><dd>{f.phone}</dd></div> : null}
              {f.place ? (
                <div><dt>Where</dt><dd>{f.place}</dd></div>
              ) : null}
              {f.campaign ? <div><dt>Campaign</dt><dd>{f.campaign}</dd></div> : null}
              {f.landing ? (
                <div>
                  <dt>Landed on</dt>
                  <dd>
                    <a href={f.landing} target="_blank" rel="noreferrer">{f.landingPath || f.landing}</a>
                  </dd>
                </div>
              ) : null}
            </div>

            {f.productList.length ? (
              <div className="reqwants">
                <dt>Asked about</dt>
                <div className="reqplans">
                  {f.productList.map(p => (
                    <span key={p} className="dealchip plan"><Icon name="tag" />{p}</span>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Every field they actually typed, as they typed it.
                The API returns the form verbatim and the sync keeps the
                marketing ones - the message, the team size, the promo code,
                the company they gave. It is the part of a lead somebody
                opens the lead to read, and until now none of it reached
                further than a line of the notes paragraph.

                Not verbatim in one respect, deliberately: pickFields on the
                server drops anything that is not a marketing attribute,
                because the tracker has been seen returning payroll and
                identity data scraped off logged-in screens. */}
            {Object.keys(f.fields).length ? (
              <div className="reqfields">
                <dt>What they filled in</dt>
                <div className="reqgrid">
                  {Object.entries(f.fields).map(([k, v]) => (
                    <div key={k}>
                      <dt>{k.replace(/\[\]$/, '').replace(/[_-]+/g, ' ')}</dt>
                      <dd>{String(v)}</dd>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Kept, because a parser that has never met a line is not a
                reason for that line to vanish. */}
            <details className="reqraw">
              <summary>As it was written</summary>
              <p className="vnotes">{d.notes}</p>
            </details>
          </>
        ) : <div className="vnone">Nothing recorded yet.</div>}
      </Panel>

    </Sheet>
  );
}
